import { PrintMenu } from '../print-menu';
import { FONT, FontStyleSheet } from '../font-style-sheets';
import { SectionColumnType } from '../../enum/shared/section-column-type';
import { SectionColumnProductInfoType } from '../../enum/shared/section-column-product-info-type';
import { SectionColumnViewModel } from '../../../modules/display/components/menus/product-menu/building-blocks/menu-section/product-section/section-column-view-models/SectionColumnViewModel';
import { SectionColumnCannabinoidType } from '../../enum/shared/section-column-cannabinoid-type';
import { Type } from '@angular/core';
import { PrintHeaderLayoutType } from '../../enum/shared/print-header-layout-type.enum';
import { PrintFooterLayoutType } from '../../enum/shared/print-footer-layout-type.enum';
import { ProductMenuFooterComponent } from '../../../modules/display/components/menus/product-menu/building-blocks/menu-footer/product-menu-footer.component';
import { ProductMenuHeaderComponent } from '../../../modules/display/components/menus/product-menu/building-blocks/menu-header/product-menu-header.component';
import { SortUtils } from '../../../utils/sort-utils';
import { Variant } from '../../product/dto/variant';
import { BadgeUtils } from '../../../utils/badge-utils';
import { Section } from '../section/section';
import { SectionRowViewModel } from '../../../modules/display/components/menus/product-menu/building-blocks/menu-section/product-section/section-row-view-models/SectionRowViewModel';
import { exists } from '../../../functions/exists';
import { StrainType } from '../../enum/dto/strain-classification.enum';
import { SortVariantUtils } from '../../../utils/sort-variant-utils';
import { EnumUtils } from '../../../utils/enum-utils';
import { SectionColumnConfigCollectiveTerpeneKey } from '../../enum/shared/section-column-config-collective-terpene-key';
import { EmptySection } from '../section/empty-section';
import { EmptySectionMatchesColorOfFollowingProductSection } from '../section/empty-section-matches-color-of-following-product-section';
import { SectionUtils } from '../../../utils/section-utils';
import { Position } from '../../enum/shared/position.enum';
import { SectionType } from '../../enum/dto/section-type.enum';
import { WithinNameCell } from '../../enum/shared/within-name-cell';
import { PlantlifeCheapWeedFooterComponent } from '../../../modules/display/components/menus/product-menu/building-blocks/menu-footer/plantlife-cheap-weed-footer/plantlife-cheap-weed-footer.component';
import { MenuHeaderPlantlifeCheapWeedEmptyComponent } from '../../../modules/display/components/menus/product-menu/building-blocks/menu-header/menu-header-plantlife/menu-header-plantlife-cheap-weed-empty/menu-header-plantlife-cheap-weed-empty.component';
import { PlantlifeCheapWeedEmptyFooterComponent } from '../../../modules/display/components/menus/product-menu/building-blocks/menu-footer/plantlife-cheap-weed-footer/plantlife-cheap-weed-empty-footer/plantlife-cheap-weed-empty-footer.component';
import { MenuHeaderPlantlifeCheapWeedComponent } from '../../../modules/display/components/menus/product-menu/building-blocks/menu-header/menu-header-plantlife/menu-header-plantlife-cheap-weed/menu-header-plantlife-cheap-weed.component';
import { RegexUtils } from '../../../utils/regex-utils';
import type { VariantBadge } from '../../product/dto/variant-badge';
import type { SectionWithProducts } from '../section/section-with-products';
import type { FixedLocationPropertyViewModel } from '../../../modules/display/components/menus/product-menu/building-blocks/menu-section/product-section/fixed-location-property-view-models/fixed-location-property-view-model';

export class PlantlifeCheapWeedMenu extends PrintMenu {
  override deserializeSections() {
    this.sections?.forEach(section => {
      const isProductSection = section?.sectionType === SectionType.Product;
      if (isProductSection) section.sectionType = SectionType.PlantlifeCheapWeedProduct;
    });
    this.sections = window?.injector?.Deserialize?.arrayOf(Section, this.sections) ?? [];
    this.sections?.forEach(section => this.transformBlendClassificationsToHydrid(section));
  }

  override deserializeTemplateSections() {
    this.templateSections?.forEach(section => {
      const isProductSection = section?.sectionType === SectionType.Product;
      if (isProductSection) section.sectionType = SectionType.PlantlifeCheapWeedProduct;
    });
    this.templateSections = window?.injector?.Deserialize?.arrayOf(Section, this.templateSections) ?? [];
    this.templateSections?.forEach(section => this.transformBlendClassificationsToHydrid(section));
  }

  private transformBlendClassificationsToHydrid(section: Section): void {
    if (SectionUtils.isSectionWithProducts(section)) {
      section?.products?.forEach(product => {
        product?.variants?.forEach(variant => {
          if (variant?.classification === StrainType?.Blend) {
            variant.classification = StrainType.Hybrid;
          }
        });
      });
    }
  }

  whatFontsAreNeededToRenderContent(): FontStyleSheet[] {
    return [FONT.BebasNeuePro.Regular_400, FONT.BebasNeuePro.SemiBold_600];
  }

  override getAdobeFontFamilyCssUrl(): string | null {
    return 'https://use.typekit.net/fgy7byg.css';
  }

  getPageTopMargin(): string {
    return '0.25in';
  }

  getPageBottomMargin(): string {
    return '0.25in';
  }

  getPageLeftMargin(): string {
    return '0.25in';
  }

  getPageRightMargin(): string {
    return '0.25in';
  }

  getThemeClass(): string {
    return 'plantlife-cheap-weed-theme';
  }

  getHeaderWrapperClass(): string {
    return 'header-container-wrapper';
  }

  getSectionsBackgroundColor(): string {
    return this.getBackgroundColorWithOpacity([0, 0, 0]);
  }

  getProductSectionBackgroundColor(section: Section): string {
    return this.getBackgroundColorWithOpacity();
  }

  getSectionTitleBorderColor(section: Section): string {
    return this.menuOptions?.bodyBackgroundColor;
  }

  override getColumnOrdering(): Map<SectionColumnType, number> {
    return new Map<SectionColumnType, number>([
      [SectionColumnProductInfoType.StrainType, 0],
      [SectionColumnProductInfoType.ProductTitle, 1],
      [SectionColumnProductInfoType.Badge, 2],
      [SectionColumnProductInfoType.Strain, 3],
      SectionColumnViewModel.getDefaultProductTagGroupColumnOrdering(4),
      ...this.getDefaultCannabinoidColumnOrdering(5),
      ...this.getDefaultTerpeneColumnOrdering(6),
      [SectionColumnProductInfoType.VariantType, 7],
      [SectionColumnProductInfoType.Stock, 8],
      [SectionColumnProductInfoType.QuantityAndSize, 9],
      [SectionColumnProductInfoType.Quantity, 10],
      [SectionColumnProductInfoType.Size, 11],
      [SectionColumnCannabinoidType.THCAndCBD, 12],
      [SectionColumnProductInfoType.GradingResults, 13],
      [SectionColumnProductInfoType.VariantPrice, 14],
      [SectionColumnProductInfoType.VariantSecondaryPrice, 15]
    ]);
  }

  getDefaultCannabinoidColumnOrdering(startingNumber: number): [SectionColumnType, number][] {
    const getOrderNumber = (index: number) => startingNumber + 0.001 * index;
    const filterCustomOrderedCannabinoids = (it: keyof typeof SectionColumnCannabinoidType) => {
      return (
        it !== SectionColumnCannabinoidType.THC &&
        it !== SectionColumnCannabinoidType.CBD &&
        it !== SectionColumnCannabinoidType.CBN &&
        it !== SectionColumnCannabinoidType.CBG &&
        it !== SectionColumnCannabinoidType.CBC &&
        it !== SectionColumnCannabinoidType.THCV
      );
    };
    const remainingCannabinoids = Object.values(SectionColumnCannabinoidType).filter(filterCustomOrderedCannabinoids);
    return [
      [SectionColumnCannabinoidType.THC, getOrderNumber(0)],
      [SectionColumnCannabinoidType.CBD, getOrderNumber(1)],
      [SectionColumnCannabinoidType.CBN, getOrderNumber(2)],
      [SectionColumnCannabinoidType.CBG, getOrderNumber(3)],
      [SectionColumnCannabinoidType.CBC, getOrderNumber(4)],
      [SectionColumnCannabinoidType.THCV, getOrderNumber(5)],
      ...remainingCannabinoids.map(
        (c, i) => [c, getOrderNumber(i + 6)] as [keyof typeof SectionColumnCannabinoidType, number]
      )
    ];
  }

  getDefaultTerpeneColumnOrdering(startingNumber: number): [SectionColumnType, number][] {
    const getOrderNumber = (index: number) => startingNumber + 0.001 * index;
    const sectionColumnTerpeneType = EnumUtils.createEnum([
      SectionColumnConfigCollectiveTerpeneKey.TotalTerpene,
      SectionColumnConfigCollectiveTerpeneKey.TopTerpene,
      ...EnumUtils.getTerpenesPascalCased()
    ]);
    return Object.values(sectionColumnTerpeneType).map((c, i) => [c, getOrderNumber(i)] as [SectionColumnType, number]);
  }

  shutOffBackgroundAsset(): boolean {
    return true;
  }

  getHeaderType(): Type<ProductMenuHeaderComponent> {
    return MenuHeaderPlantlifeCheapWeedComponent;
  }

  getPrintHeaderLayoutType(): PrintHeaderLayoutType {
    return this.menuOptions?.printHeaderLayout || PrintHeaderLayoutType.AllPages;
  }

  getPrintFooterLayoutType(): PrintFooterLayoutType {
    return this.menuOptions?.printFooterLayout || PrintFooterLayoutType.AllPagesFixed;
  }

  getShowFooter(): boolean {
    return true;
  }

  getFooterType(): Type<ProductMenuFooterComponent> {
    return PlantlifeCheapWeedFooterComponent;
  }

  override isFixedLocationBadgesPropertyCellVisible(data: FixedLocationPropertyViewModel[] | null): boolean {
    return true;
  }

  override fixedLocationBadgesPropertyIsWithin(): WithinNameCell {
    return WithinNameCell.TitleLine;
  }

  getShowBadgesInline(data: FixedLocationPropertyViewModel[] | null): WithinNameCell | null {
    return WithinNameCell.TitleLine;
  }

  getEmptyHeaderReplacement(): Type<ProductMenuHeaderComponent> {
    return MenuHeaderPlantlifeCheapWeedEmptyComponent;
  }

  getEmptyFooterReplacement(): Type<ProductMenuFooterComponent> {
    return PlantlifeCheapWeedEmptyFooterComponent;
  }

  pushFooterToBottomOfPage(): boolean {
    const footerLayoutType = this.getPrintFooterLayoutType();
    return footerLayoutType === PrintFooterLayoutType.AllPagesFixed;
  }

  getUniqueClassificationList(): StrainType[] {
    return super.getUniqueClassificationList()?.sort(SortVariantUtils.plantlifeStrainTypeSort);
  }

  getEmptySectionType(): Type<EmptySection> {
    return EmptySectionMatchesColorOfFollowingProductSection;
  }

  override getShouldInflateColumnForRow(s: Section, col: SectionColumnViewModel, row: SectionRowViewModel): boolean {
    const badge = col?.columnType === SectionColumnProductInfoType.Badge;
    return !badge || (badge && exists(row?.hasBadges()));
  }

  protected override getVariantBadgesForLineItems(
    section: SectionWithProducts,
    lineItems: Variant[] | Variant[][]
  ): VariantBadge[] {
    const sectionType = section?.layoutType;
    const variantBadgeMap = section?.variantBadgeMap;
    return (lineItems || ([] as any[]))
      ?.map(lineItem => {
        if (lineItem instanceof Array) {
          return BadgeUtils.getBadges(sectionType, 0, variantBadgeMap, lineItem)?.sort(SortUtils.sortBadgesByName);
        } else if (lineItem instanceof Variant) {
          return BadgeUtils.getBadges(sectionType, 0, variantBadgeMap, [lineItem]);
        } else {
          return [];
        }
      })
      ?.flatten<VariantBadge[]>()
      ?.filter(variantBadge => {
        const badgeName = variantBadge?.name?.toLowerCase();
        if (!badgeName) return false;
        return !RegexUtils.cheapWeedRegex.test(badgeName);
      });
  }

  primaryPriceColumnAlsoShowOriginalPriceIfOnSale(section: SectionWithProducts): boolean {
    return true;
  }

  primaryPriceColumnAlsoShowOriginalPriceIfOnSalePosition(): Position {
    return null;
  }
}
